"use client";

import { useState, useRef, type UIEvent } from "react";
import { CopyButton } from "~/components/_pages/tools/prompts/copyButton";
import CardUI from "~/components/ui/card";
import GreeterUI from "~/components/ui/greeter";
// آدرس کامپوننت کپی را بر اساس پروژه خود تنظیم کنید
import { cn } from "~/lib/utils";

const HIGHLIGHT_COLORS = [
  "bg-red-400/100",
  "bg-sky-400/60",
  "bg-green-400/60",
  "bg-yellow-400/60",
  "bg-purple-400/60",
  "bg-pink-400/60",
  "bg-teal-400/60",
];

export default function BinaryConverterPage() {
  const [text, setText] = useState("");
  const [binary, setBinary] = useState("");
  const [direction, setDirection] = useState<"rtl" | "ltr">("rtl");

  const textBackdropRef = useRef<HTMLDivElement>(null);
  const binBackdropRef = useRef<HTMLDivElement>(null);

  const getDirection = (val: string) => {
    if (!val) return "rtl";
    const persianRegex = /^[\u0600-\u06FF\s]+/;
    return persianRegex.test(val.trim()) ? "rtl" : "ltr";
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newText = e.target.value;
    setText(newText);
    setDirection(getDirection(newText));

    const newBinary = Array.from(newText)
      .map((char) => char.codePointAt(0)?.toString(2).padStart(8, "0") ?? "")
      .join(" ");
    
    setBinary(newBinary);
  };

  const handleBinaryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newBinary = e.target.value;
    setBinary(newBinary);

    const newText = newBinary
      .split(/\s+/)
      .filter((bin) => bin.length > 0)
      .map((bin) => {
        try {
          const charCode = parseInt(bin, 2);
          if (isNaN(charCode)) return "";
          return String.fromCodePoint(charCode);
        } catch {
          return ""; 
        }
      })
      .join("");

    setText(newText);
    setDirection(getDirection(newText));
  };

  const handleTextScroll = (e: UIEvent<HTMLTextAreaElement>) => {
    if (textBackdropRef.current) {
      textBackdropRef.current.scrollTop = e.currentTarget.scrollTop;
      textBackdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  const handleBinaryScroll = (e: UIEvent<HTMLTextAreaElement>) => {
    if (binBackdropRef.current) {
      binBackdropRef.current.scrollTop = e.currentTarget.scrollTop;
      binBackdropRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  };

  let binColorIndex = 0;
  const renderBinaryHighlights = () => {
    return binary.split(/(\s+)/).map((segment, index) => {
      if (/^\s+$/.test(segment)) {
        return <span key={index}>{segment}</span>;
      }
      const colorClass = HIGHLIGHT_COLORS[binColorIndex % HIGHLIGHT_COLORS.length];
      binColorIndex++;
      return (
        <span key={index} className={cn(colorClass, "rounded-sm")}>
          {segment}
        </span>
      );
    });
  };

  return (
    <div className="container mx-auto max-w-7xl pb-32" dir="rtl">
      <GreeterUI className="mb-8">
        <GreeterUI.Sub text="تبدیل همزمان و دقیق متن و کدهای باینری" />
        <GreeterUI.Title text="مبدل باینری" />
      </GreeterUI>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CardUI
          variant="morph"
          className="flex h-80 md:h-125 flex-col overflow-hidden rounded-xl text-left shadow-sm"
          dir={direction}
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3" dir="rtl">
            <span className="font-semibold text-white">متن (Text)</span>
            <CopyButton promptText={text} />
          </div>
          
          <div className="relative flex-1">
            <div
              ref={textBackdropRef}
              className={cn(
                "pointer-events-none absolute inset-0 overflow-auto p-4 font-mono text-lg leading-relaxed whitespace-pre-wrap wrap-break-word text-transparent",
                direction === "rtl" && "text-right"
              )}
            >
              {Array.from(text).map((char, index) => (
                <span
                  key={`text-${index}`}
                  className={cn(HIGHLIGHT_COLORS[index % HIGHLIGHT_COLORS.length], "rounded-sm")}
                >
                  {char}
                </span>
              ))}
            </div>
            <textarea
              value={text}
              onChange={handleTextChange}
              onScroll={handleTextScroll}
              placeholder="متن خود را اینجا تایپ کنید..."
              className="absolute inset-0 h-full w-full resize-none bg-transparent p-4 font-mono text-lg leading-relaxed text-white caret-white outline-none"
              spellCheck={false}
            />
          </div>
        </CardUI>

        <CardUI
          variant="morph"
          className="flex h-80 md:h-125 flex-col overflow-hidden rounded-xl text-left shadow-sm"
          dir="ltr"
        >
          <div className="flex items-center justify-between border-b border-white/10 px-4 py-3" dir="rtl">
            <span className="font-semibold text-white">باینری (Binary)</span>
            <CopyButton promptText={binary} />
          </div>

          <div className="relative flex-1">
            <div
              ref={binBackdropRef}
              className="pointer-events-none absolute inset-0 overflow-auto p-4 font-mono text-lg leading-relaxed whitespace-pre-wrap wrap-break-word text-transparent"
            >
              {renderBinaryHighlights()}
            </div>
            <textarea
              value={binary}
              onChange={handleBinaryChange}
              onScroll={handleBinaryScroll}
              placeholder="01101000 01101001..."
              className="absolute inset-0 h-full w-full resize-none bg-transparent p-4 font-mono text-lg leading-relaxed text-white caret-white outline-none"
              spellCheck={false}
            />
          </div>
        </CardUI>
      </div>
    </div>
  );
}